import type { AddressAutocompleteItem } from '../../../core/models/address-autocomplete';

/** One rendered text segment for an autocomplete option row. */
export interface AddressAutocompleteTextSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Returns true when Canada Post returned a container row that requires LastId drill-down.
 */
export function isAddressAutocompleteFindItem(item: AddressAutocompleteItem): boolean {
  return item.next.trim().toLowerCase() === 'find';
}

/**
 * Builds the single-line dropdown label shown for an autocomplete suggestion.
 */
export function formatAddressAutocompleteOptionLabel(item: AddressAutocompleteItem): string {
  return `${item.text}, ${item.description}`;
}

/**
 * Parses Canada Post highlight ranges (for example `0-3,4-17`) into render segments.
 */
export function buildAddressAutocompleteHighlightSegments(
  text: string,
  highlight: string,
): AddressAutocompleteTextSegment[] {
  if (!highlight.trim()) {
    return [{ text, highlighted: false }];
  }

  const ranges = highlight
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [startRaw, endRaw] = part.split('-');
      const start = Number(startRaw);
      const end = Number(endRaw);
      return Number.isFinite(start) && Number.isFinite(end) ? { start, end } : null;
    })
    .filter((range): range is { start: number; end: number } => range !== null)
    .sort((left, right) => left.start - right.start);

  if (ranges.length === 0) {
    return [{ text, highlighted: false }];
  }

  const segments: AddressAutocompleteTextSegment[] = [];
  let cursor = 0;

  for (const range of ranges) {
    const safeStart = Math.max(0, Math.min(range.start, text.length));
    const safeEnd = Math.max(safeStart, Math.min(range.end + 1, text.length));

    if (safeStart > cursor) {
      segments.push({ text: text.slice(cursor, safeStart), highlighted: false });
    }

    if (safeEnd > safeStart) {
      segments.push({ text: text.slice(safeStart, safeEnd), highlighted: true });
    }

    cursor = Math.max(cursor, safeEnd);
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlighted: false });
  }

  return segments.filter((segment) => segment.text.length > 0);
}
